/**
 * Resolves the estimate's labor crews from the estimate export: each line item's crew for the
 * reports, and every crew's makeup for the `__LaborCrews` data table. Used by the estimate ETL, and
 * by the UPC labor crews admin table for its rows.
 */
import { EstimateData, LaborResourceRateType } from "../../interfaces/EstimateData";
import { LineItem } from "../../interfaces/LineItem";
import { RegionalLaborCrew } from "../../interfaces/Product";

interface CrewBlendedRate {
  cbr: number;
  ibr: number;
  crewSize: number;
}

interface CrewCompositionRateRow {
  quantity: number | null;
  rate: number | null;
}

/** One resource rate in a crew's makeup, with the resource it prices. */
interface LaborCrewMember extends CrewCompositionRateRow {
  resourceId: string;
  resourceName: string;
  resourceItemCode: string | null;
  resourceFromUpc: boolean;
  rateType: LaborResourceRateType;
}

interface LaborCrew extends CrewBlendedRate {
  id: string;
  name: string;
  itemCode: string | null;
  fromUpc: boolean;
  members: LaborCrewMember[];
}

/**
 * A `__LaborCrews` (or `admin_UPCLaborCrews`) row: one per crew member, with the crew's columns
 * repeated, so a `SUMIFS` on `crew_id` over `extended_rate` gives back `crew_rate`. A crew with no
 * members gets one row with the member columns blank, so every crew a line item names is in the table.
 */
export interface LaborCrewRow {
  crew_id: string;
  crew_description: string;
  crew_item_code: string | null;
  crew_source: "UPC" | "Custom";
  crew_size: number;
  crew_rate: number;
  individual_rate: number;
  resource_id: string | null;
  resource_description: string | null;
  resource_item_code: string | null;
  resource_source: "UPC" | "Custom" | null;
  rate_type: LaborResourceRateType | null;
  rate: number | null;
  quantity: number | null;
  extended_rate: number | null;
}

/**
 * The fields the report and data tables read: `\labor_crew`, `\individual_rate`, and the size the
 * calculated columns scale by.
 */
interface LineItemLaborCrewFields {
  labor_crew: string;
  individual_rate: number | null;
  crew_size: number | null;
}

const RATE_TYPE_ORDER: Record<LaborResourceRateType, number> = { REGULAR: 0, OVERTIME: 1, DOUBLE: 2 };

/**
 * Copy of `computeBlendedRateFromComposition` in `common/utils/crewBlendedRate.ts`, which the
 * add-in can't import; keep the two in step. CBR = Σ(quantity × rate); null rates are excluded
 * from CBR but still counted in crew_size. Null quantities are excluded from both. IBR = CBR /
 * crew_size, zero when crew_size is zero. Plain floats where the app uses Decimal: nothing here
 * is persisted, and Excel formats the cell.
 */
function computeBlendedRateFromComposition(rows: CrewCompositionRateRow[]): CrewBlendedRate {
  let cbr = 0;
  let crewSize = 0;
  for (const row of rows) {
    if (row.quantity === null) continue;
    crewSize += row.quantity;
    if (row.rate !== null) {
      cbr += row.quantity * row.rate;
    }
  }
  return { cbr, crewSize, ibr: crewSize === 0 ? 0 : cbr / crewSize };
}

/** Replaces an absent or non-finite crew size with a single worker, as the app does for an unassigned row. */
export const toUsableCrewSize = (crewSize: number | null | undefined): number =>
  crewSize != null && Number.isFinite(crewSize) ? crewSize : 1;

const toNumberOrNull = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Every live crew in the estimate, by id, with its members and blended rate. The export's crew
 * tables are raw rows filtered only by estimate, so soft-deleted crews, makeup rows, rates and
 * resources are skipped here; left in, a removed crew member would inflate CBR and crew size. A
 * makeup row whose rate or resource is gone is omitted, as the app's own CBR does.
 */
const buildLaborCrewLookup = (data: EstimateData): Map<string, LaborCrew> => {
  const isLive = (row: { deleted_at: string | null }) => row.deleted_at == null;
  const resourceById = new Map(
    (data.estimate_labor_resources ?? []).filter(isLive).map((resource) => [resource.id, resource])
  );
  const rateById = new Map((data.estimate_labor_resource_rates ?? []).filter(isLive).map((rate) => [rate.id, rate]));
  const membersByCrew = new Map<string, LaborCrewMember[]>();
  for (const row of (data.estimate_labor_crew_resource_quantities ?? []).filter(isLive)) {
    const rate = rateById.get(row.estimate_labor_resource_rate);
    const resource = rate && resourceById.get(rate.estimate_labor_resource);
    if (!rate || !resource) continue;
    const members = membersByCrew.get(row.estimate_labor_crew) ?? [];
    members.push({
      resourceId: resource.id,
      resourceName: resource.name ?? "",
      resourceItemCode: resource.item_code,
      resourceFromUpc: resource.upc_resource != null,
      rateType: rate.type,
      rate: toNumberOrNull(rate.rate),
      quantity: toNumberOrNull(row.quantity),
    });
    membersByCrew.set(row.estimate_labor_crew, members);
  }
  return new Map(
    (data.estimate_labor_crews ?? []).filter(isLive).map((crew) => {
      const members = membersByCrew.get(crew.id) ?? [];
      return [
        crew.id,
        {
          id: crew.id,
          name: crew.name ?? "",
          itemCode: crew.item_code,
          fromUpc: crew.upc_crew != null,
          members,
          ...computeBlendedRateFromComposition(members),
        },
      ];
    })
  );
};

const compareText = (a: string, b: string) => a.localeCompare(b);

const compareMembers = (a: LaborCrewMember, b: LaborCrewMember) =>
  compareText(a.resourceName, b.resourceName) || RATE_TYPE_ORDER[a.rateType] - RATE_TYPE_ORDER[b.rateType];

const toLaborCrewRows = (crew: LaborCrew): LaborCrewRow[] => {
  const crewColumns = {
    crew_id: crew.id,
    crew_description: crew.name,
    crew_item_code: crew.itemCode,
    crew_source: crew.fromUpc ? "UPC" : "Custom",
    crew_size: crew.crewSize,
    crew_rate: crew.cbr,
    individual_rate: crew.ibr,
  } as const;
  const blankMember = {
    resource_id: null,
    resource_description: null,
    resource_item_code: null,
    resource_source: null,
    rate_type: null,
    rate: null,
    quantity: null,
    extended_rate: null,
  };
  if (crew.members.length === 0) return [{ ...crewColumns, ...blankMember }];
  return [...crew.members].sort(compareMembers).map((member) => ({
    ...crewColumns,
    resource_id: member.resourceId,
    resource_description: member.resourceName,
    resource_item_code: member.resourceItemCode,
    resource_source: member.resourceFromUpc ? "UPC" : "Custom",
    rate_type: member.rateType,
    rate: member.rate,
    quantity: member.quantity,
    extended_rate: member.quantity !== null && member.rate !== null ? member.quantity * member.rate : null,
  }));
};

const sortedLaborCrewRows = (crews: Iterable<LaborCrew>): LaborCrewRow[] =>
  Array.from(crews)
    .sort((a, b) => compareText(a.name, b.name))
    .flatMap(toLaborCrewRows);

/** The `__LaborCrews` rows: every live crew's members, crews by name and members by resource. */
export const transformLaborCrews = (data: EstimateData): LaborCrewRow[] =>
  sortedLaborCrewRows(buildLaborCrewLookup(data).values());

/**
 * The UPC catalog crews in one region, as `__LaborCrews` rows. Every crew and resource here is
 * from the UPC. The blended rates are the API's own for the region, not recomputed.
 */
export const transformRegionalLaborCrews = (crews: RegionalLaborCrew[]): LaborCrewRow[] =>
  sortedLaborCrewRows(
    crews.map((crew) => ({
      id: crew.id,
      name: crew.description ?? "",
      itemCode: crew.item_code,
      fromUpc: true,
      cbr: crew.cbr,
      ibr: crew.ibr,
      crewSize: crew.crew_size,
      members: (crew.labor_crew_resource_quantities ?? []).map((member) => ({
        resourceId: member.labor_resource,
        resourceName: member.resource_description ?? "",
        resourceItemCode: member.item_code,
        resourceFromUpc: true,
        rateType: member.rate_type,
        rate: toNumberOrNull(member.rate),
        quantity: toNumberOrNull(member.quantity),
      })),
    }))
  );

/**
 * Sets each line item's crew fields from the crew it's assigned. A line item with no crew, or
 * whose crew was deleted, gets a blank crew, no Individual Rate and no crew size, so the calculated columns
 * treat it as one worker.
 *
 * An assembly parent is left without a crew, as in the app, but takes the crew size of its selected
 * child (`selected_traditional_assembly`). The parent's quantity and `prod_rate` already come from
 * that child (`calculateAssemblyParentRates` in `common/utils/assemblyParentRollup.ts`), so its
 * Crew Hours are that child's, and its individual-hour columns follow the same child. A parent with
 * no selected child counts as one worker.
 */
export const assignLaborCrewFields = (lineItems: LineItem[], data: EstimateData): void => {
  const crews = buildLaborCrewLookup(data);
  const parentIds = new Set<string>();
  const selectedChildCrewSize = new Map<string, number | null>();

  for (const item of lineItems) {
    const crew = item.estimate_labor_crew ? crews.get(item.estimate_labor_crew) : undefined;
    const fields: LineItemLaborCrewFields = {
      labor_crew: crew?.name ?? "",
      individual_rate: crew ? crew.ibr : null,
      crew_size: crew ? crew.crewSize : null,
    };
    Object.assign(item, fields);

    if (item.parent) {
      parentIds.add(item.parent);
      if (item.selected_traditional_assembly) selectedChildCrewSize.set(item.parent, fields.crew_size);
    }
  }

  for (const item of lineItems) {
    if (!parentIds.has(item.id)) continue;
    const fields: LineItemLaborCrewFields = {
      labor_crew: "",
      individual_rate: null,
      crew_size: selectedChildCrewSize.get(item.id) ?? null,
    };
    Object.assign(item, fields);
  }
};
